<template>
  <div>
    <h2 class="mb-5">{{ defaultHeader }}</h2>

    <div v-if="isMainTenantOrganization">
      <h4>{{ translationNoDepRegistered }}</h4>
    </div>
    <!-- No deposit -->
    <div v-else-if="deposits.length === 0 && !loading.fetchDeposits">
      <h4>{{ translationNoDepRegistered }}</h4>
      <p>{{ translationSelectOneOf }}</p>
      <div class="mt-4">
        <BlockContainer class="p-5 text-center">
          <div class="icon-container">
            <i class="budicon-solid budicon-solid-bank"></i>
          </div>
          <div class="mt-4 pt-2">
            <h5>{{ translationNewDepositAccount }}</h5>
            <p class="mt-4">
              {{ translationCreateDepAccount }}
            </p>
          </div>
          <div class="mt-5">
            <hl-Button variant="primary" @click="startDepositAccount">
              {{ translationCreate }}
            </hl-Button>
          </div>
        </BlockContainer>
      </div>

      <div class="my-4">
        <BlockContainer class="p-5 text-center">
          <h6>{{ translationOrRegisterAnother }}</h6>
          <hl-Select
            class="mt-4 pt-2 other-deposit-select"
            :options="otherDepositOptions"
            placeholder="Velg alternativ"
            v-model="newOtherDeposit"
          />
          <transition name="fade">
            <button v-if="newOtherDeposit" class="btn btn-primary mt-4" @click="addNewDeposit">Opprett</button>
          </transition>
        </BlockContainer>
      </div>
    </div>
    <!-- / NO deposit -->

    <template v-for="deposit in deposits">
      <DepositSubscriptionCardContainer
        v-if="isDepositSubscription(deposit)"
        :key="deposit.id"
        :deposit="deposit"
        class="mb-4"
      />
      <DepositAccountCardContainer
        v-else-if="isDepositAccountV2(deposit)"
        :key="deposit.id"
        :landlord-id="parseInt(landlordId, 10)"
        :tenancy-id="parseInt(tenancyId, 10)"
        :deposit="deposit"
        class="mb-4"
      />
      <InstabankCardContainer
        v-else-if="isInstabankDeposit(deposit)"
        :key="deposit.id"
        :landlord-id="parseInt(landlordId, 10)"
        :tenancy-id="parseInt(tenancyId, 10)"
        :deposit="deposit"
        class="mb-4"
      />
      <TenancyDepositComponent
        v-else
        :key="deposit.id"
        :deposit="deposit"
        :landlordId="landlordId"
        :tenancyId="tenancyId"
        :isEditMode="deposit.status === 'new'"
        class="mb-4"
        @removeDraft="removeDraftDeposit"
      />
    </template>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import depositDefaults from '@/assets/consts';
import BlockContainer from '@/components/BlockContainer.vue';
import TenancyDepositComponent from '@/components/tenancy/deposit/TenancyDeposit';
import Button from '@modules/husleie-framework/src/components/buttons/Button.vue';
import Select from '@modules/husleie-framework/src/components/forms/inputs/select/Select.vue';
import i18nCommon from '@modules/i18n/mixins/Common';
import { AppRedirect } from '@modules/husleie-framework/src/enums/AppRedirect.enum';
import RedirectOperations from '@modules/husleie-framework/src/static-classes/RedirectOperations.class';
import { LandlordTypeEnum } from '@modules/husleie-framework/src/types/Landlord.entity';
import Textarea from '@modules/design-system/components/atoms/Textarea.vue';
import DepositAccountCardContainer from '@/components/deposit/depositAccount/DepositAccountCardContainer.vue';
import InstabankCardContainer from '@/components/deposit/depositAccount/InstabankCardContainer.vue';
import DepositSubscriptionCardContainer from '@/components/deposit/depositSubscription/DepositSubscriptionCardContainer.vue';

export default Vue.extend({
  name: 'TenancyDeposit',
  components: {
    BlockContainer,
    DepositAccountCardContainer,
    InstabankCardContainer,
    DepositSubscriptionCardContainer,
    TenancyDepositComponent,
    'hl-Button': Button,
    'hl-Select': Select,
    'a-Textarea': Textarea,
  },
  mixins: [i18nCommon],
  props: ['landlordId', 'tenancyId', 'defaultHeader'],

  data() {
    return {
      deposits: [],
      loading: {
        fetchDeposits: false,
      },
      newOtherDeposit: '',
    };
  },

  computed: {
    ...mapGetters('tenancy', ['getTenancyDeposits', 'getTenancy']),
    ...mapGetters(['getLandlordProfileMeta']),

    otherDepositOptions() {
      return [
        {
          name: 'Depositumsgaranti via Husleie.no',
          value: 'deposit_guarantee',
        },
        { name: 'Annen depositumsgaranti', value: 'other_deposit_guarantee' },
        {
          name: 'Depositumskonto i annen bank',
          value: 'other_deposit_account',
        },
        { name: 'Bankgaranti', value: 'bank_guarantee' },
        { name: 'NAV-garanti', value: 'nav_guarantee' },
        { name: 'Annen form for sikkerhet', value: 'other' },
      ];
    },
    isMainTenantOrganization() {
      return ![this.getTenancy.tenants[0].organizationName, this.getTenancy.tenants[0].organizationNumber].includes(
        undefined
      );
    },
    translationNoDepRegistered() {
      return this.translate('tenancy.deposit.no deposit registered on the tenancy');
    },
    translationSelectOneOf() {
      return this.translate('tenancy.deposit.select one of the options under');
    },
    translationNewDepositAccount() {
      return this.translate('tenancy.deposit.new deposit account');
    },
    translationCreateDepAccount() {
      return this.translate('tenancy.deposit.create deposit account');
    },
    translationCreate() {
      return this.translate('tenancy.deposit.create');
    },
    translationOrRegisterAnother() {
      return this.translate('tenancy.deposit.or register another form of deposit or security');
    },
  },

  methods: {
    ...mapActions('tenancy', ['fetchTenancyDeposits']),

    startDepositAccount() {
      const landlordMeta = this.getLandlordProfileMeta(this.landlordId);
      const isCompany = landlordMeta.organizationNumber;
      const tenancy = this.getTenancy;
      const tenant = tenancy.tenants.find(tenant => tenant.type === 'tenant');

      if (isCompany) {
        RedirectOperations.goToURL(
          AppRedirect.DEPOSIT_ACCOUNT,
          {
            landlordId: this.landlordId,
            landlordType: LandlordTypeEnum.PROFESSIONAL,
            landlordName: landlordMeta.fullName,
            rentalObjectId: tenancy.rentalObjectId,
            tenancyId: tenancy.id,
            tenantName: tenant.fullName,
            tenantEmail: tenant.email,
            tenantCellphone: tenant.cellPhone,
            companyName: landlordMeta.meta.organization.name,
            companyOrgNumber: landlordMeta.organizationNumber,
            rentRent: tenancy.rent.rent,
            rentDuration: tenancy.endDate != null ? 'limited' : 'unlimited',
            rentStartdate: tenancy.startDate,
            rentEnddate: tenancy.endDate,
          },
          true
        );
      } else {
        RedirectOperations.goToURL(
          AppRedirect.DEPOSIT_ACCOUNT,
          {
            landlordId: this.landlordId,
            landlordType: LandlordTypeEnum.PRIVATE,
            landlordName: landlordMeta.fullName,
            rentalObjectId: this.getTenancy.rentalObjectId,
            tenancyId: this.getTenancy.id,
            tenantName: tenant.fullName,
            tenantEmail: tenant.email,
            tenantCellphone: tenant.cellPhone,
            rentRent: tenancy.rent.rent,
            rentDuration: tenancy.endDate != null ? 'limited' : 'unlimited',
            rentStartdate: tenancy.startDate,
            rentEnddate: tenancy.endDate,
          },
          true
        );
      }
      this.currentWizard = null;
    },

    isDepositAccountV2(deposit) {
      return deposit.category === 'deposit_leabank';
    },

    isInstabankDeposit(deposit) {
      return deposit.category === 'deposit_instabank';
    },

    isDepositSubscription(deposit) {
      return deposit.category === 'upsell_deposit_subscription';
    },

    addTenancyDeposit(cat) {
      const defaultConfig = depositDefaults.depositDefaults[cat];
      const UUIDv4 = function b(a) {
        return a
          ? (a ^ ((Math.random() * 16) >> (a / 4))).toString(16)
          : ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, b);
      };
      const uuid = UUIDv4();
      defaultConfig.id = uuid;
      this.deposits.unshift(defaultConfig);
    },

    addNewDeposit() {
      this.addTenancyDeposit(this.newOtherDeposit);
    },

    removeDraftDeposit(id) {
      const index = this.deposits
        .map(x => {
          return x.id;
        })
        .indexOf(id);

      this.deposits.splice(index, 1);
    },

    goToRentalObject() {
      this.$router.push({
        name: 'rentalObject_tenancies',
        params: { rentalObjectId: this.getTenancy.rentalObjectId, landlordId: this.landlordId },
      });
    },
  },

  async mounted() {
    this.loading.fetchDeposits = true;
    try {
      await this.fetchTenancyDeposits({
        landlordId: this.landlordId,
        tenancyId: this.tenancyId,
      });
    } catch (error) {
      console.error(error);
      this.$toast('Kunne ikke hente depositum.', 'error');
    }
    this.loading.fetchDeposits = false;
  },

  watch: {
    getTenancyDeposits(list) {
      this.deposits = [...list].sort(a => {
        return a.status === 'active' ? -1 : 0;
      });
    },
  },
});
</script>

<style scoped lang="scss">
.icon-container {
  background: $gray-200;
  border-radius: 50%;
  padding: 1rem;
  display: flex;
  width: 60px;
  height: 60px;
  color: $gray-800;
  align-items: center;
  justify-content: center;
  margin: auto;

  i {
    font-size: 1.4rem;
  }
}

.border-radius-medium {
  border-radius: $border-radius-medium;
}

.border-gray {
  border: 1px solid $gray-200;
}

.box-shadow-2 {
  box-shadow: $box-shadow-2;
}

.other-deposit-select {
  max-width: 420px;
  margin: auto;
}

.dots-icon {
  background: $gray-200;
  border-radius: 50%;
  height: 20px;
}
</style>
